import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";
import DriftingGhost from "../Ghost";

/**
 * Marauder's Map — the site's real navigation.
 *
 * Opens on the incantation, folds away on "Mischief managed". Every location
 * on the parchment is a route, and a trail of footprints walks from the castle
 * to wherever you currently are.
 */

interface Place {
  path: string;
  name: string;
  sub: string;
  x: number;   // percentage across the parchment
  y: number;
}

const PLACES: Place[] = [
  { path: "/",           name: "The Great Hall",       sub: "Home",        x: 50, y: 44 },
  { path: "/skills",     name: "Hogwarts Grounds",     sub: "Skills",      x: 25, y: 57 },
  { path: "/experience", name: "The Hogwarts Express", sub: "Experience",  x: 13, y: 80 },
  { path: "/projects",   name: "The Restricted Section", sub: "Projects",  x: 64, y: 21 },
  { path: "/leetcode",   name: "The Duelling Club",    sub: "LeetCode",    x: 78, y: 34 },
  { path: "/opensource", name: "The Forbidden Forest", sub: "Open Source", x: 22, y: 22 },
  { path: "/education",  name: "Examination Hall",     sub: "Education",   x: 45, y: 74 },
  { path: "/contact",    name: "The Owlery",           sub: "Contact",     x: 85, y: 62 },
];

const CASTLE = { x: 50, y: 44 };

const MaraudersMap: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const close = useCallback(() => {
    setClosing(true);
    window.setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, 650);
  }, []);

  // Esc folds the map away, same as the incantation
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  const go = useCallback((path: string) => {
    navigate(path);
    close();
  }, [navigate, close]);

  const active = PLACES.find((p) => p.path === location.pathname) || PLACES[0];

  // Footprints walking from the castle out to wherever you are
  const trail = useMemo(() => {
    const steps = 6;
    return Array.from({ length: steps }, (_, i) => {
      const t = (i + 1) / (steps + 1);
      return {
        x: CASTLE.x + (active.x - CASTLE.x) * t,
        y: CASTLE.y + (active.y - CASTLE.y) * t,
        side: i % 2 === 0 ? -1 : 1,
        i,
      };
    });
  }, [active]);

  return (
    <>
      <Trigger onClick={() => setOpen(true)} aria-label="Open the Marauder's Map">
        <TriggerGlyph>&#128506;</TriggerGlyph>
        <TriggerText>The Map</TriggerText>
      </Trigger>

      {open && (
        <Backdrop $closing={closing} onClick={close}>
          <Parchment $closing={closing} onClick={(e) => e.stopPropagation()}>
            <Oath>I solemnly swear that I am up to no good.</Oath>

            <MapCanvas>
              {/* Ink-drawn grounds */}
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                <defs>
                  <linearGradient id="mm_lake" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="rgba(80,110,130,0.22)" />
                    <stop offset="100%" stopColor="rgba(60,90,110,0.08)" />
                  </linearGradient>
                </defs>

                {/* Black Lake */}
                <ellipse cx="80" cy="72" rx="19" ry="12" fill="url(#mm_lake)"
                  stroke="rgba(90,70,40,0.5)" strokeWidth="0.4" />

                {/* Forbidden Forest — a scatter of little conifers */}
                {Array.from({ length: 16 }).map((_, i) => {
                  const x = 8 + (i % 6) * 4.4 + (i % 3) * 1.4;
                  const y = 12 + Math.floor(i / 6) * 5.5 + (i % 2) * 1.6;
                  return (
                    <path key={i} d={`M${x} ${y + 4} L${x + 1.7} ${y} L${x + 3.4} ${y + 4} Z`}
                      fill="rgba(70,55,30,0.28)" stroke="rgba(90,70,40,0.45)" strokeWidth="0.25" />
                  );
                })}

                {/* Castle footprint */}
                <rect x="41" y="35" width="18" height="15" fill="rgba(120,95,50,0.12)"
                  stroke="rgba(90,70,40,0.62)" strokeWidth="0.5" />
                <path d="M43 35 L43 30 L46 30 L46 35 M54 35 L54 28 L57 28 L57 35"
                  fill="rgba(120,95,50,0.12)" stroke="rgba(90,70,40,0.62)" strokeWidth="0.5" />

                {/* Railway line down to the station */}
                <path d="M8 88 Q24 80 34 66 Q42 56 45 50"
                  fill="none" stroke="rgba(90,70,40,0.45)" strokeWidth="0.5" strokeDasharray="2 1.4" />

                {/* Paths radiating from the castle */}
                {PLACES.filter((p) => p.path !== "/").map((p) => (
                  <path key={p.path}
                    d={`M${CASTLE.x} ${CASTLE.y} Q${(CASTLE.x + p.x) / 2 + 4} ${(CASTLE.y + p.y) / 2 - 5} ${p.x} ${p.y}`}
                    fill="none" stroke="rgba(90,70,40,0.28)" strokeWidth="0.35" strokeDasharray="1.2 1.2" />
                ))}
              </svg>

              {/* Footprint trail to wherever you are */}
              {trail.map((f) => (
                <Foot key={f.i} $x={f.x} $y={f.y} $side={f.side} $i={f.i}>
                  &#128099;
                </Foot>
              ))}

              {/* Locations */}
              {PLACES.map((p) => {
                const isActive = p.path === active.path;
                return (
                  <Spot key={p.path} $x={p.x} $y={p.y} $active={isActive}
                    onClick={() => go(p.path)}>
                    <SpotDot $active={isActive} />
                    <SpotLabel $active={isActive}>
                      <SpotName>{p.name}</SpotName>
                      <SpotSub>{p.sub}</SpotSub>
                    </SpotLabel>
                  </Spot>
                );
              })}

              <YouAreHere $x={active.x} $y={active.y}>You are here</YouAreHere>

              {/* the resident ghost, drifting through */}
              <DriftingGhost name="Nearly Headless Nick" tone="ink" duration={20} band={[14, 30]} />
            </MapCanvas>

            <Signatories>
              Messrs. Moony, Wormtail, Padfoot &amp; Prongs
              <em>Purveyors of Aids to Magical Mischief-Makers</em>
            </Signatories>

            <MischiefButton onClick={close}>Mischief managed.</MischiefButton>
          </Parchment>
        </Backdrop>
      )}
    </>
  );
};

export default MaraudersMap;

/* ==================== styles ==================== */

const unfurl = keyframes`
  from { opacity: 0; transform: scaleY(0.05) rotateX(35deg); }
  60%  { opacity: 1; transform: scaleY(1.03) rotateX(0deg); }
  to   { opacity: 1; transform: scaleY(1) rotateX(0deg); }
`;

const furl = keyframes`
  from { opacity: 1; transform: scaleY(1); }
  to   { opacity: 0; transform: scaleY(0.05); }
`;

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const fadeOut = keyframes`from { opacity: 1; } to { opacity: 0; }`;

const stepIn = keyframes`
  from { opacity: 0; transform: scale(0.5); }
  to   { opacity: 0.55; transform: scale(1); }
`;

const inkPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(160, 0, 0, 0.4); }
  50%      { box-shadow: 0 0 0 7px rgba(160, 0, 0, 0); }
`;

const Trigger = styled.button`
  position: fixed;
  right: 1.4rem;
  bottom: 1.4rem;
  z-index: 120;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 0.9rem;
  border-radius: 100px;
  border: 1px solid rgba(196, 148, 42, 0.4);
  background: rgba(20, 14, 10, 0.82);
  backdrop-filter: blur(10px);
  color: #d9b45a;
  font-family: "Cinzel", serif;
  font-size: 0.62rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  cursor: none;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(196, 148, 42, 0.85);
    box-shadow: 0 0 22px rgba(196, 148, 42, 0.25);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    right: 0.8rem;
    bottom: 0.8rem;
    padding: 0.45rem 0.7rem;
  }
`;

const TriggerGlyph = styled.span`font-size: 0.85rem; line-height: 1;`;
const TriggerText = styled.span`
  @media (max-width: 480px) { display: none; }
`;

const Backdrop = styled.div<{ $closing: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(4, 2, 8, 0.82);
  backdrop-filter: blur(4px);
  animation: ${({ $closing }) => ($closing ? fadeOut : fadeIn)} 0.45s ease both;
`;

const Parchment = styled.div<{ $closing: boolean }>`
  position: relative;
  width: min(940px, 100%);
  max-height: 92vh;
  overflow-y: auto;
  padding: 2rem 2.2rem 1.5rem;
  transform-origin: center top;
  animation: ${({ $closing }) => ($closing ? furl : unfurl)}
             ${({ $closing }) => ($closing ? "0.6s" : "0.8s")}
             cubic-bezier(0.22, 1, 0.36, 1) both;

  background:
    repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(139,105,20,0.03) 3px, rgba(139,105,20,0.03) 4px),
    linear-gradient(145deg, #f0dfbe 0%, #e6d2a6 35%, #ecdab4 65%, #dfc999 100%);
  border: 1px solid #b8892a;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.7), inset 0 0 60px rgba(150, 110, 40, 0.22);

  /* Worn, folded edges */
  clip-path: polygon(
    0% 2%, 3% 0%, 20% 2%, 38% 0%, 55% 2%, 72% 0%, 88% 2%, 100% 0%,
    100% 98%, 96% 100%, 80% 98%, 62% 100%, 45% 98%, 28% 100%, 10% 98%, 0% 100%
  );

  @media (max-width: 600px) { padding: 1.5rem 1rem 1rem; }
`;

const Oath = styled.p`
  text-align: center;
  font-family: "Caveat", cursive;
  font-size: clamp(1rem, 2.6vw, 1.4rem);
  color: #6b4a1a;
  margin-bottom: 1rem;
`;

const MapCanvas = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  border: 1px solid rgba(139, 105, 20, 0.35);
  border-radius: 2px;

  svg { position: absolute; inset: 0; width: 100%; height: 100%; }

  @media (max-width: 600px) { aspect-ratio: 3 / 4; }
`;

const Foot = styled.span<{ $x: number; $y: number; $side: number; $i: number }>`
  position: absolute;
  left: ${({ $x }) => $x}%;
  top: ${({ $y }) => $y}%;
  transform: translate(-50%, -50%) translateX(${({ $side }) => $side * 5}px)
             rotate(${({ $side }) => $side * 18}deg);
  font-size: 0.6rem;
  opacity: 0.55;
  pointer-events: none;
  animation: ${stepIn} 0.35s ease both;
  animation-delay: ${({ $i }) => 0.35 + $i * 0.09}s;
`;

const Spot = styled.button<{ $x: number; $y: number; $active: boolean }>`
  position: absolute;
  left: ${({ $x }) => $x}%;
  top: ${({ $y }) => $y}%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  background: none;
  border: none;
  cursor: none;
  padding: 0.25rem;
  transition: transform 0.25s ease;

  &:hover { transform: translate(-50%, -50%) scale(1.12); }
`;

const SpotDot = styled.span<{ $active: boolean }>`
  width: ${({ $active }) => ($active ? "10px" : "7px")};
  height: ${({ $active }) => ($active ? "10px" : "7px")};
  border-radius: 50%;
  background: ${({ $active }) => ($active ? "#a00000" : "#6b4a1a")};
  border: 1px solid rgba(60, 40, 10, 0.6);
  transition: all 0.25s ease;
  ${({ $active }) => $active && css`animation: ${inkPulse} 1.8s ease-in-out infinite;`}
`;

const SpotLabel = styled.span<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.15;
  opacity: ${({ $active }) => ($active ? 1 : 0.82)};
`;

const SpotName = styled.span`
  font-family: "Cinzel", serif;
  font-size: 0.56rem;
  font-weight: 600;
  color: #3d2a10;
  white-space: nowrap;

  @media (max-width: 600px) { font-size: 0.48rem; }
`;

const SpotSub = styled.span`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.42rem;
  color: #8a6a2a;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const YouAreHere = styled.span<{ $x: number; $y: number }>`
  position: absolute;
  left: ${({ $x }) => $x}%;
  top: ${({ $y }) => $y}%;
  transform: translate(-50%, -230%);
  font-family: "Caveat", cursive;
  font-size: 0.8rem;
  color: #a00000;
  white-space: nowrap;
  pointer-events: none;
`;





const Signatories = styled.p`
  text-align: center;
  font-family: "Caveat", cursive;
  font-size: 0.95rem;
  color: #7a5a20;
  margin-top: 1rem;
  line-height: 1.4;

  em { display: block; font-size: 0.75rem; color: #8f7038; }
`;

const MischiefButton = styled.button`
  display: block;
  margin: 0.9rem auto 0;
  font-family: "Cinzel", serif;
  font-size: 0.7rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 0.55rem 1.4rem;
  border-radius: 3px;
  border: 1px solid rgba(139, 105, 20, 0.5);
  background: rgba(139, 105, 20, 0.09);
  color: #5c3f13;
  cursor: none;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(139, 105, 20, 0.2);
    border-color: rgba(139, 105, 20, 0.85);
  }
`;
